// The Mirabilis report's "brief" view: an editorial layout that grows with a reading-depth
// slider (Brief → The Full Story). This module derives what the brief says from
// the report data the standard view already uses; it adds no content of its own.

import { fmtPct } from "../forecast/formatPct.js";
import { citedCardsOf, rankByReliance, reasonReferencesOf } from "../forecast/evidenceCards.js";
import { triggerTickerOf, triggerWhenOf } from "../forecast/reportAnalysis.js";
import { strengthWord } from "./evidenceStrength.js";
import { bucketLabel, leadingRow } from "../forecast/multiway.js";

export const BRIEF_LEVELS = [
  { id: 1, name: "Brief", note: "Answer, reasons, numbers" },
  { id: 2, name: "Full analysis", note: "Every section, the evidence and the methodology" },
];

const STRENGTH_POINTS = { weak: 1, moderate: 2, strong: 3 };
const pct = (p) => `${fmtPct(p)}%`;

/** "a" or "an" before a percentage as it is read aloud: "an 18% chance", "a 21% chance". */
export function chanceArticle(pctText) {
  return /^(?:8|11(?!\d)|18(?!\d))/.test(String(pctText)) ? "an" : "a";
}

/**
 * A yes/no forecast's headline: the larger side's chance (Yes at 50%), stated on the question's
 * own event — "Sayer puts a 72% chance on a Fed cut at the June 2027 meeting", "Sayer puts an
 * 82% chance against a Fed cut at the June 2027 meeting." `event` is that event as a noun phrase
 * (written per question by the small LLM); without one the sentence still states the chance.
 */
export function yesNoHeadline(probability, event = "") {
  if (typeof probability !== "number" || !Number.isFinite(probability)) return "";
  const yes = probability >= 0.5;
  const p = pct(yes ? probability : 1 - probability);
  const what = String(event || "").trim() || "this happening";
  return `Sayer puts ${chanceArticle(p)} ${p} chance ${yes ? "on" : "against"} ${what}.`;
}

/**
 * A distribution's rule-built headline. The catch-all can outweigh the named leader without
 * outweighing the whole named field; the leader then only "leads the named field".
 */
function headlineForField({ lead, other, otherLeads }) {
  if (lead.isOther === true) return `An outcome not listed is most likely, at ${pct(lead.p)}.`;
  const name = bucketLabel(lead);
  if (otherLeads)
    return `${name} leads the named field at ${pct(lead.p)}, with ${pct(other.p)} on outcomes not listed.`;
  return `${name} is most likely, at ${pct(lead.p)}.`;
}
const words = (t) =>
  String(t || "")
    .split(/\s+/)
    .filter(Boolean).length;
// A key figure that is only a year ("2035") is a date, not a number worth a tile.
const YEAR = /^(?:19|20)\d\d$/;

/** "2,853,299" → "2.85M"; a figure that is not a plain big number passes through. */
export function compactFigure(value) {
  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n) || Math.abs(n) < 10000) return value;
  return n.toLocaleString("en-US", { notation: "compact", maximumFractionDigits: 2 });
}

/**
 * How far a trigger would move the forecast: for a yes/no question the gap between P(YES) if
 * it happens and if it doesn't; for a distribution its distance between those two cases.
 */
export function triggerImpact(ev) {
  if (typeof ev?.a === "number" && typeof ev?.b === "number") return Math.abs(ev.a - ev.b);
  if (typeof ev?.dist_impact === "number") return ev.dist_impact;
  const a = ev?.a_pmf;
  const b = ev?.b_pmf;
  if (a && b && typeof a === "object" && typeof b === "object")
    return Math.max(0, ...Object.keys(a).map((k) => Math.abs((a[k] ?? 0) - (b[k] ?? 0))));
  return 0;
}

/** The strongest item of a column (the first among equals). */
function strongest(items) {
  let best = null;
  for (const item of items || []) {
    const s = STRENGTH_POINTS[strengthWord(item?.strength)] || 0;
    if (!best || s > best.s) best = { item, s };
  }
  return best?.item ?? null;
}

/**
 * What the brief says, from the report's own data.
 *
 * @param {object} args
 * @param {number|null} args.probability P(YES), for a yes/no question
 * @param {Array<{label: string, p: number}>|null} args.buckets the distribution, otherwise
 * @param {{left: object, right: object}|null} args.columns mirabilisInsightColumns output
 * @param {Array} args.cards evidenceCardsOf(r)
 * @param {Map} args.citations citationsByCard(columns)
 * @param {Array} args.events the report's triggers
 * @param {string} args.narrative Sooth's view
 * @param {{verdict?: string}|null} args.outside outsideViewsOf output
 * @param {Array<{value: string, label: string, card_id: string}>|null} [args.headlineFigures]
 *   the figures the report writer chose for this question (analysis.headline_figures)
 * @param {Date} [args.today]
 */
export function buildBrief({
  probability = null,
  buckets = null,
  columns = null,
  cards = [],
  citations = new Map(),
  events = [],
  narrative = "",
  outside = null,
  headlineFigures = null,
  today = new Date(),
}) {
  const multi =
    Array.isArray(buckets) && buckets.filter((b) => typeof b?.p === "number").length > 1;
  const sorted = multi
    ? [...buckets].filter((b) => typeof b?.p === "number").sort((a, b) => b.p - a.p)
    : typeof probability === "number"
      ? [
          { label: "Yes", p: probability },
          { label: "No", p: 1 - probability },
        ].sort((a, b) => b.p - a.p)
      : [];
  // The same leader the standard report draws: a catch-all leads only when it outweighs
  // every named outcome together. The rest follow by probability, so the runner-up is never
  // the leader.
  const lead = multi ? leadingRow(sorted) : (sorted[0] ?? null);
  // A yes/no question always reads Yes first; a distribution reads leader first.
  const dist = !multi
    ? ["Yes", "No"].map((label) => sorted.find((d) => d.label === label)).filter(Boolean)
    : lead
      ? [lead, ...sorted.filter((d) => d !== lead)]
      : sorted;
  // The catch-all, when it outweighs the named leader: the leader then only leads the named
  // field, and no headline may call it "most likely".
  const other = multi ? (sorted.find((d) => d?.isOther === true) ?? null) : null;
  const otherLeads = !!(multi && lead && lead !== other && other && other.p > lead.p);
  const headline = !lead
    ? ""
    : multi
      ? headlineForField({ lead, other, otherLeads })
      : yesNoHeadline(probability, "");

  const left = columns?.left?.items ?? [];
  const right = columns?.right?.items ?? [];
  // The viewer's local day, as date-only triggers are read elsewhere — not the UTC day.
  const todayIso = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
  const upcoming = (events || [])
    .filter(
      // A date compares at its own precision: "2026-10" is still upcoming all October.
      (ev) => {
        const when = ev && !ev.isPast ? triggerWhenOf(ev).date : "";
        return !!when && when >= todayIso.slice(0, when.length);
      },
    )
    .sort((a, b) => (triggerWhenOf(a).date < triggerWhenOf(b).date ? -1 : 1));
  const next = upcoming[0] ?? null;
  // The triggers that matter most — impact weighted by how likely each is — read in date order.
  const ranked = [...upcoming].sort(
    (x, y) =>
      triggerImpact(y) * (typeof y.p_event === "number" ? y.p_event : 0.5) -
      triggerImpact(x) * (typeof x.p_event === "number" ? x.p_event : 0.5),
  );
  const topTriggers = ranked
    .slice(0, 3)
    .sort((x, y) => (triggerWhenOf(x).date < triggerWhenOf(y).date ? -1 : 1));

  const { cited, background } = rankByReliance(cards, citations);
  const reliabilities = cards.map((c) => c.reliability).filter((x) => typeof x === "number");
  // The report writer's own picks for this question when the report has them; otherwise the
  // relied-on cards' decisive figures, primary cards first, never a bare year.
  const byId = new Map(cards.map((c) => [c.cardId, c]));
  const chosen = (Array.isArray(headlineFigures) ? headlineFigures : [])
    .filter(
      (f) => f && typeof f.value === "string" && typeof f.label === "string" && byId.has(f.card_id),
    )
    .slice(0, 4)
    .map((f) => ({
      card: byId.get(f.card_id),
      value: compactFigure(f.value),
      raw: f.value,
      label: f.label,
    }));
  const keyNumbers = chosen.length
    ? chosen
    : [...cited.map((x) => x.card), ...background]
        .filter((c) => c.keyStat && !YEAR.test(c.keyStat.value))
        .slice(0, 4)
        .map((c) => ({
          card: c,
          value: compactFigure(c.keyStat.value),
          raw: c.keyStat.value,
          label: c.keyStat.label,
        }));

  const allText = [...left, ...right].map((p) => p.detail || p.claim).join(" ");
  const minutes = {
    1: Math.max(
      1,
      Math.round((60 + words([left[0]?.claim, right[0]?.claim, next?.title].join(" "))) / 230),
    ),
    2: Math.max(1, Math.round((220 + words(narrative) + words(allText)) / 230)),
    3: Math.max(
      1,
      Math.round(
        (220 + words(narrative) + words(allText) + words(cards.map((c) => c.body).join(" "))) / 230,
      ),
    ),
  };

  return {
    multi,
    distribution: dist,
    lead,
    other,
    otherLeads,
    headline,
    why: strongest(left),
    risk: strongest(right),
    caseFor: left,
    caseAgainst: right,
    caseForPrefix: columns?.left?.prefix || "L",
    caseAgainstPrefix: columns?.right?.prefix || "F",
    caseForTitle: String(columns?.left?.label || ""),
    caseAgainstTitle: String(columns?.right?.label || ""),
    upcoming,
    topTriggers,
    next,
    nextWhen: next ? triggerWhenOf(next).date : null,
    tickerOf: (ev) => triggerTickerOf(ev, multi ? null : probability, multi ? buckets : null),
    // Only a real comparison: no line when no market, expert or institution gives a number.
    consensus: outside?.numericCount > 0 ? outside.verdict || "" : "",
    evidence: {
      sources: cards.length,
      primary: cited.length,
      avgReliability: reliabilities.length
        ? reliabilities.reduce((a, b) => a + b, 0) / reliabilities.length
        : null,
    },
    keyNumbers,
    sourcesOf: (item) => citedCardsOf(item?.citations, cards),
    // The cards and direct links a reason cites, for its references.
    referencesOf: (item) => reasonReferencesOf(item?.citations, cards),
    minutes,
  };
}

/**
 * The words for how likely the forecast's answer is, so every headline is calibrated the same
 * way: the leading outcome of a distribution, or a yes/no question's answer.
 * @param {{ multi: boolean, p: number }} forecast `p` is the lead's probability, or P(YES)
 */
export function likelihoodPhrase({ multi, p }) {
  if (multi) return p >= 0.6 ? "is likely to" : "is most likely to";
  if (p >= 0.9) return "will almost certainly";
  if (p >= 0.65) return "is likely to";
  if (p === 0.5) return "is as likely as not to";
  if (p >= 0.5) return "is slightly more likely than not to";
  if (p > 0.35) return "is slightly more likely not to";
  if (p > 0.1) return "is unlikely to";
  return "is very unlikely to";
}

// The criteria are context, not the headline: enough to say what a number measures.
const MEASURE_MAX = 700;

/**
 * The prompt asking for the brief's headline: the forecast's conclusion as one sentence. A
 * numeric outcome's `unit` and the question's resolution `criteria` say what its range
 * measures — a difference between two things, say, rather than either one's own value.
 */
export function conclusionPrompt({
  question,
  multi,
  lead,
  runnerUp = null,
  probability = null,
  unit = "",
  criteria = "",
}) {
  const p = multi ? lead.p : probability;
  const phrase = likelihoodPhrase({ multi, p });
  const forecast = multi
    ? `Most likely outcome: ${lead.label}${runnerUp ? `; next: ${runnerUp.label}` : ""}.`
    : p === 0.5
      ? "The forecast is evenly balanced between yes and no."
      : `The forecast leans ${p > 0.5 ? "yes" : "no"}.`;
  return [
    "Write the headline of a forecast report: ONE plain declarative sentence stating the forecast's conclusion as the answer to the question.",
    `Use the question's own wording for what is predicted, and express how likely it is with this phrase: "${phrase}" (adjust only its grammar to fit the sentence, e.g. "are most likely to").`,
    !multi
      ? "Turn the yes/no question into a statement of what is expected to happen (or not happen)."
      : /\d/.test(lead.label)
        ? `The most likely outcome is a number or range (${lead.label}${unit ? `, in ${unit}` : ""}): make the thing being measured the subject and state the range in plain words (e.g. "The 10-year yield is most likely to be between 4.0% and 4.25% on …", "Between 10 and 15 states are most likely to …"). Read the resolution criteria for what the number measures: when it is a difference or gap between two things, say so (e.g. "The Lexus IS 500 is most likely to retain 10 to 15 percentage points more of its original MSRP than the BMW M3 …").`
        : `Make the most likely outcome (${lead.label}) the subject.`,
    "Do not state the forecast's probability; keep any number that is part of the question itself. No hedging beyond that phrase, no preamble, no quotation marks. At most 30 words.",
    "",
    `Question: ${question}`,
    ...(multi && criteria
      ? [
          `Resolution criteria: ${String(criteria).replace(/\s+/g, " ").trim().slice(0, MEASURE_MAX)}`,
        ]
      : []),
    forecast,
    "",
    "Headline:",
  ].join("\n");
}

// Every calibrated phrase, longest first, by its core words: the headline must use exactly
// the forecast's own one ("are most likely to" still counts as "is most likely to").
const PHRASE_CORES = [
  "slightly more likely than not to",
  "as likely as not to",
  "slightly more likely not to",
  "will almost certainly",
  "very unlikely to",
  "most likely to",
  "unlikely to",
  "likely to",
];
const coreOf = (phrase) => phrase.replace(/^(?:is|are)\s+/, "");

/** The calibrated phrase a sentence uses, by the longest core it contains; null for none. */
function phraseIn(text) {
  const t = ` ${text.toLowerCase()} `;
  return PHRASE_CORES.find((core) => new RegExp(`[^a-z]${core}[^a-z]`).test(t)) ?? null;
}

/**
 * A model's headline, cleaned to one sentence; null when it is unusable: empty, too long, more
 * than one sentence, not using the forecast's own likelihood phrase (when given), or giving a
 * percentage that neither the question nor the predicted outcome gives — that would be the
 * forecast's probability, which the brief shows elsewhere.
 */
export function cleanConclusion(text, question = "", outcome = "", phrase = "") {
  const line = String(text || "")
    .split("\n")
    .map((l) => l.trim())
    .find(Boolean);
  if (!line) return null;
  const t = line
    .replace(/^headline:\s*/i, "")
    .replace(/^["'“”*]+|["'“”*]+$/g, "")
    .trim();
  if (!t || t.length > 260) return null;
  // One sentence: no full stop before another sentence (initials such as "U.S." don't count).
  if (/(?<![A-Z])[.!?]\s+[A-Z]/.test(t.replace(/[.!?]+$/, ""))) return null;
  if (phrase && phraseIn(t) !== coreOf(phrase)) return null;
  // A named outcome must be the sentence's subject: "Samsung is likely to lead…" is not a
  // conclusion about a TSMC forecast. (A range is written in words, so it is not checked.)
  if (outcome && !/\d/.test(outcome)) {
    const subject = t.replace(/^the\s+/i, "").toLowerCase();
    if (!subject.startsWith(outcome.toLowerCase())) return null;
  }
  // Figures the question or the outcome itself gives ("at least 4.4%", "[4.0, 4.25)").
  const given = new Set(`${question} ${outcome}`.match(/\d+(?:\.\d+)?/g)?.map(Number) ?? []);
  const percents = t.match(/\d+(?:\.\d+)?(?=\s*(?:%|percent\b))/gi)?.map(Number) ?? [];
  if (percents.some((x) => !given.has(x))) return null;
  // The probability in any other form ("a probability of 0.72", "with 0.72 confidence",
  // "3-to-1 odds", "72 in 100") is still the probability.
  if (
    /\b(?:probability|probabilities|chance|chances|odds|confidence|certainty|likelihood)\b/i.test(t)
  )
    return null;
  // "3 to 1" is odds, but "10 to 15" spelling out the outcome's own range is not.
  const odds = [
    ...t.matchAll(/(\d+(?:\.\d+)?)\s*(?:in|out of|to|-to-)\s*(\d+(?:\.\d+)?)/gi),
  ].filter((m) => !/^\d{4}$/.test(m[1]) && !(given.has(Number(m[1])) && given.has(Number(m[2]))));
  if (odds.length) return null;
  const fractions = t.match(/(?<![\d.])0?\.\d+\b/g)?.map(Number) ?? [];
  if (fractions.some((x) => !given.has(x))) return null;
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/**
 * The headline without a model: a "Which/What/Who … will …?" question rewritten around the
 * leading outcome ("Which chipmaker will lead X?" → "TSMC is most likely to lead X.", "Who will be
 * the nominee?" → "Alice is most likely to be the nominee."). Null for any other shape — or an
 * inverted one such as "What will GDP be?" — where a rule can't safely find the predicate.
 */
export function ruleConclusion({ question, multi, lead }) {
  if (!multi || !lead || lead.isOther === true) return null;
  const m = String(question || "")
    .trim()
    .match(/^(?:which|what|who)\b[^?]*?\bwill\s+(.+?)\?*$/i);
  if (!m) return null;
  const predicate = m[1].replace(/\s+/g, " ");
  // "What will GDP be?" asks for a value of its own subject: no predicate to hand the lead.
  if (/\b(?:be|do|have)$/i.test(predicate)) return null;
  return `${lead.label} ${likelihoodPhrase({ multi, p: lead.p })} ${predicate}.`;
}

/** "2030-01-31" → "January 31, 2030"; anything else passes through unchanged. */
export function longDate(iso) {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(String(iso || ""));
  if (!m) return iso || "";
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3] || 1)));
  return date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "long",
    ...(m[3] ? { day: "numeric" } : {}),
    year: "numeric",
  });
}

/**
 * The brief's headline sentence: the model's conclusion when it has one, else the rule-built
 * sentence, else the plain answer.
 * @param {{conclusion: string|null, question: string, brief: ReturnType<typeof buildBrief>}} args
 */
export function briefHeadlineOf({ conclusion, question, brief: b }) {
  // When the catch-all outweighs the leader, only the rule-built "leads the named field"
  // sentence is honest: no written or rule headline may call the leader "most likely".
  if (b.otherLeads || b.lead?.isOther === true) return b.headline;
  return conclusion || ruleConclusion({ question, multi: b.multi, lead: b.lead }) || b.headline;
}

/**
 * The prompt for a yes/no headline's event: the question's own event as a noun phrase, which
 * completes "Sayer puts an N% chance on ___." The number is set by the app, never the model.
 */
export function yesNoEventPrompt({ question, criteria = "" }) {
  return [
    "Rewrite a yes/no forecasting question as a short noun phrase naming the event it asks about, so it completes this sentence:",
    '"Sayer puts an N% chance on ___."',
    'Use the question\'s own terms: keep its names, dates and thresholds, but say it briefly (e.g. "a Fed rate cut at the June 2027 meeting", "Bitcoin closing above $150,000 on December 31, 2026", "the Lakers winning the 2027 NBA Finals").',
    "Start lowercase unless the first word is a name. At most 16 words. No probability, no hedging, no full stop, no quotation marks, no preamble.",
    "",
    `Question: ${question}`,
    ...(criteria
      ? [
          `Resolution criteria: ${String(criteria).replace(/\s+/g, " ").trim().slice(0, MEASURE_MAX)}`,
        ]
      : []),
    "",
    "Phrase:",
  ].join("\n");
}

/** A model's event phrase, cleaned; null when it is unusable (too long, a chance, a sentence). */
export function cleanEventPhrase(text, question = "") {
  const line = String(text || "")
    .split("\n")
    .map((l) => l.trim())
    .find(Boolean);
  if (!line) return null;
  let t = line
    .replace(/^phrase:\s*/i, "")
    .replace(/^sayer puts an? [\d.]+% chance on\s+/i, "")
    .replace(/^["'“”*]+|["'“”*]+$/g, "")
    .replace(/[.\s]+$/, "")
    .trim();
  if (!t || t.length > 160 || t.split(/\s+/).length > 20 || /[?!]/.test(t)) return null;
  if (/\b(?:chance|chances|probability|odds|likely|likelihood|unlikely)\b/i.test(t)) return null;
  // Any percentage must be the question's own (a threshold), never a forecast.
  const given = new Set(
    String(question)
      .match(/\d+(?:\.\d+)?/g)
      ?.map(Number) ?? [],
  );
  const percents = t.match(/\d+(?:\.\d+)?(?=\s*(?:%|percent\b))/gi)?.map(Number) ?? [];
  if (percents.some((x) => !given.has(x))) return null;
  // An article or common word starts lowercase; a name keeps its capital.
  if (/^(?:A|An|The|No|Any|At|Its|Their)\b/.test(t)) t = t[0].toLowerCase() + t.slice(1);
  return t;
}

/**
 * The brief's "bottom line" rows, as the screen and the exports both show them. "Why" and "The
 * risk" are the written lines (useBriefBottomLine) when they have arrived; until then they are
 * `pending`, and if writing fails they fall back to the strongest reason and the first open
 * question.
 * @param {ReturnType<typeof buildBrief>} b
 * @param {{written?: {why: string, risk: string}|null, uncertainties?: string[], pending?: boolean,
 *   totalSources?: number|null}} [opts] totalSources: every source the report cites
 * @returns {Array<{label: string, text: string, note?: string, pending?: boolean}>}
 */
export function bottomLineOf(
  b,
  { written = null, uncertainties = [], pending = false, totalSources = null } = {},
) {
  const evidence = b.evidence;
  const row = (label, text) =>
    pending ? { label, text: "", pending: true } : text ? { label, text } : null;
  // Every source the report cites (as Methodology lists them), then the ones written up as
  // evidence cards; without a total, the cards stand for the sources.
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const total = Number.isInteger(totalSources) && totalSources > 0 ? totalSources : null;
  const evidenceText = [
    total != null && plural(total, "source", "sources"),
    evidence.sources > 0 &&
      (total != null
        ? plural(evidence.sources, "evidence card", "evidence cards")
        : plural(evidence.sources, "source", "sources")),
    evidence.sources > 0 && `${evidence.primary} primary`,
    evidence.avgReliability != null &&
      `average reliability ${evidence.avgReliability.toFixed(1)}/5`,
  ]
    .filter(Boolean)
    .join(" · ");
  return [
    row("Why", written?.why || b.why?.claim),
    row("The risk", written?.risk || uncertainties[0] || b.risk?.claim),
    b.next && { label: "Watch", text: watchLineOf(b) },
    (total != null || evidence.sources > 0) && { label: "Evidence", text: evidenceText },
  ].filter(Boolean);
}

/**
 * The next trigger to watch, in one or two sentences: what it is, how likely and by when, then
 * how the forecast moves if it happens and if it doesn't.
 * @param {ReturnType<typeof buildBrief>} b
 * @returns {string}
 */
export function watchLineOf(b) {
  const ev = b.next;
  if (!ev) return "";
  const pct = (p) => `${fmtPct(p)}%`;
  const title = String(ev.title || "")
    .trim()
    .replace(/[.\s]+$/, "");
  const when = b.nextWhen ? `by ${longDate(b.nextWhen)}` : "";
  const likely = typeof ev.p_event === "number" ? `${pct(ev.p_event)} likely` : "";
  const odds = [likely, when].filter(Boolean).join(" ");
  const what = odds ? `${title} — ${odds}.` : `${title}.`;

  const ticker = b.tickerOf(ev);
  const [ifYes, ifNo] = ["yes", "no"].map((key) => ticker?.find((t) => t.key === key));
  // A name takes the possessive ("Paris’s chance"); a number or range reads "the chance of 2.5% to 3%".
  const chance = (label) =>
    /^other\b/i.test(label)
      ? "the chance of an outcome not listed"
      : label === "Yes" || /\d/.test(label)
        ? `the chance of ${label === "Yes" ? "yes" : label}`
        : label.split(/\s+/).length > 3 || /^(no|none|neither)\b/i.test(label)
          ? `the chance of “${label}”`
          : `${label}’s chance`;
  const move = (m, subject) =>
    `${subject} ${m.delta > 0 ? "rises" : "falls"} from ${pct(m.to - m.delta)} to ${pct(m.to)}`;
  const yesMove = ifYes?.moves[0];
  if (!yesMove) return what;
  let impact = `If it happens, ${move(yesMove, chance(yesMove.label))}`;
  if (ifYes.newLead) impact += `, making ${ifYes.newLead} the most likely outcome`;
  const noMove = ifNo?.moves.find((m) => m.label === yesMove.label) ?? ifNo?.moves[0];
  impact += noMove
    ? `; if not, ${noMove.label === yesMove.label ? "it" : chance(noMove.label)} ${noMove.delta > 0 ? "rises" : "slips"} to ${pct(noMove.to)}.`
    : "; if not, little changes.";
  return `${what} ${impact}`;
}

/**
 * The prompt for the brief's bottom line: "why" condenses the written assessment to its main
 * rationale; "risk" recaps the biggest open question(s). Only the report's own words go in.
 * @param {{question: string, narrative: string, uncertainties: string[]}} args
 */
export function bottomLinePrompt({ question, narrative, uncertainties }) {
  return [
    "Write the bottom line of a forecast report and the view that follows it. Return only JSON:",
    '{"why": "...", "risk": "...", "view": "..."}',
    "",
    "- why: one sentence, or two when the main reason needs it, of at most 50 words in all, giving the main reason for the forecast's answer, condensed from the assessment. Keep its decisive fact or figure.",
    "- risk: ONE sentence of at most 30 words recapping the biggest open question (or two) below that could change the outcome.",
    "- view: one paragraph of 80 to 170 words, read right after why and risk. Do not repeat them: start from what they leave out (the other considerations, how the evidence weighs against itself, what would have to happen for the answer to change), and touch the main reason or risk only to add something new. Keep every fact and figure exactly as the assessment states it, but never state the forecast's probability or any outcome's chance: the report shows those beside it.",
    "Use only what is written below: no new facts or figures. Plain words; no preamble, no markdown, no quotation marks. Never mention a forecaster, model, analysis, research or sources.",
    "",
    `Question: ${question}`,
    "",
    "Assessment:",
    narrative,
    "",
    "Open questions:",
    ...(uncertainties.length
      ? uncertainties.map((u) => `- ${u}`)
      : ["(none listed: take the risk from the biggest caveat the assessment itself names)"]),
  ].join("\n");
}

/**
 * A model's bottom line, as `{why, risk}`; null unless both are short plain text ("why" may run
 * to two sentences, so it gets more room).
 */
export function cleanBottomLine(text, source = "") {
  const raw = String(text || "");
  const json = raw.slice(raw.indexOf("{"), raw.lastIndexOf("}") + 1);
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  const line = (v, max) => {
    const t = typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "";
    return t && t.length <= max && !/[*#`]/.test(t) ? t : null;
  };
  const why = line(parsed?.why, 420);
  const risk = line(parsed?.risk, 280);
  if (!why || !risk) return null;
  // Like the view, the bottom line may state only figures it was given.
  if (source && !(figuresAllGiven(why, source) && figuresAllGiven(risk, source))) return null;
  // The view is optional: without a usable one the brief shows the assessment as written.
  const view = line(parsed?.view, VIEW_MAX);
  const usable = view && !STATES_A_CHANCE.test(view) && (!source || figuresAllGiven(view, source));
  return usable ? { why, risk, view } : { why, risk };
}

// "58 percent likelihood", "a 53% chance", "probability of 0.4": the report shows the
// forecast's numbers itself, and an assessment's copy of them can be out of date.
const CHANCE_NOUN = String.raw`(?:likelihood|chances?|probabilit(?:y|ies)|odds|confidence|certainty)`;
const CHANCE_WORD = String.raw`(?:likely|${CHANCE_NOUN})`;
const PERCENT = String.raw`\d+(?:\.\d+)?\s*(?:%|percent\b)`;
const STATES_A_CHANCE = new RegExp(
  [
    // "a 58% chance", "58 percent likely", "58% probability"
    String.raw`${PERCENT}[^.;]{0,30}?\b${CHANCE_WORD}\b`,
    // "the odds are 58%", "a probability around 58%", "likelihood of 58 percent" — not
    // "likely", which before a figure describes the outcome ("likely to stay near 3.5%").
    String.raw`\b${CHANCE_NOUN}\b[^.;\d]{0,30}${PERCENT}`,
    // "a chance of 0.4", "probability is 0.58"
    String.raw`\b${CHANCE_WORD}\s+(?:of|at|is|around|about|near)\s+(?:about\s+|around\s+)?\d`,
  ].join("|"),
  "i",
);

const VIEW_MAX = 1400;
const FIGURES = /\d+(?:[.,]\d+)*/g;
const bare = (n) => n.replace(/,/g, "");
/** Every figure in `text` is one `source` also states: the view may add no numbers. */
function figuresAllGiven(text, source) {
  const given = new Set((source.match(FIGURES) || []).map(bare));
  return (text.match(FIGURES) || []).every((n) => given.has(bare(n)));
}

/**
 * The sentence on a card that states one of its figures, for a key number's hover: the first
 * sentence of the card's summary or body holding the figure as written (digits compared without
 * separators), else the card's summary, else its title.
 * @param {{summary?: string, body?: string, title?: string}|null} card
 * @param {string} figure the figure as the card writes it ("2,853,299", "72.5%")
 */
export function figureContextOf(card, figure) {
  if (!card) return "";
  const bare = (t) => String(t || "").replace(/(?<=\d)[,\s](?=\d{3}\b)/g, "");
  const want = bare(figure).trim();
  const sentences = [card.summary, card.body]
    .filter((t) => typeof t === "string" && t.trim())
    .flatMap((t) => t.replace(/\s+/g, " ").split(/(?<=[.!?])\s+(?=[A-Z0-9"“(])/));
  const hit = want ? sentences.find((sentence) => bare(sentence).includes(want)) : null;
  return (hit || card.summary || card.title || "").trim();
}

/**
 * What each collapsible brief section says while closed, in two parts the eye can tell apart:
 * `stats`, the section's counts and facts as short {value, label} pairs ("7" "upcoming"), and
 * `lines`, one to three labelled sentences ({label, text}: "Most likely" …) that sum it up. A section
 * with nothing to say gets neither.
 *
 * @param {object} args
 * @param {ReturnType<typeof buildBrief>} args.brief
 * @param {Array} args.events the report's triggers, past ones included
 * @param {{verdict?: string, reason?: string, views?: Array}|null} args.outside outsideViewsOf output
 * @param {string[]} args.uncertainties the open questions
 * @param {{forecaster?: string|null, sources?: number, resolvesOn?: string|null}|null} args.method
 * @param {ReturnType<import("../forecast/causalChains.js").causalChainsOf>} [args.chains]
 * @returns {Record<"chains"|"triggers"|"consensus"|"uncertainties"|"methodology",
 *   {stats: Array<{value: string, label: string}>, lines: Array<{label: string, text: string}>}>}
 */
export function briefSectionSummaries({
  brief: b,
  events = [],
  outside = null,
  uncertainties = [],
  method = null,
  chains = null,
}) {
  const pct = (p) => `${fmtPct(p)}%`;
  const plural = (n, one, many) => (n === 1 ? one : many);
  const stat = (value, label) => ({ value: String(value), label });
  const line = (label, text) => ({ label, text });
  const when = (ev) => {
    const d = triggerWhenOf(ev).date;
    return d ? ` by ${longDate(d)}` : "";
  };
  const likely = (ev) => (typeof ev.p_event === "number" ? ` — ${pct(ev.p_event)} likely` : "");

  const past = events.filter((ev) => ev?.isPast).length;
  const triggers = { stats: [], lines: [] };
  if (b.upcoming.length) triggers.stats.push(stat(b.upcoming.length, "upcoming"));
  if (past) triggers.stats.push(stat(past, plural(past, "past event", "past events")));
  // The upcoming trigger most likely to happen; the earlier one on a tie.
  const prob = (ev) => (typeof ev.p_event === "number" ? ev.p_event : -1);
  const mostLikely = b.upcoming.reduce(
    (top, ev) => (!top || prob(ev) > prob(top) ? ev : top),
    null,
  );
  if (mostLikely)
    triggers.lines.push(
      line("Most likely", `${mostLikely.title}${likely(mostLikely)}${when(mostLikely)}`),
    );
  // The trigger that would move the forecast most, when it is not the most likely one.
  const biggest = [...b.upcoming].sort((x, y) => triggerImpact(y) - triggerImpact(x))[0];
  const move = biggest && biggest !== mostLikely ? b.tickerOf(biggest)?.[0]?.moves?.[0] : null;
  if (move)
    triggers.lines.push(
      line(
        "Biggest swing",
        // A range reads "the chance of 15% to 20%", so its own "to" isn't mistaken for the move.
        `${biggest.title} — would move ${/\d/.test(move.label) ? `the chance of ${move.label}` : move.label} to ${pct(move.to)}`,
      ),
    );

  const text = (t) => (typeof t === "string" ? t.trim() : "");
  const views = Array.isArray(outside?.views) ? outside.views.length : 0;
  const consensus = {
    stats: views ? [stat(views, plural(views, "outside view", "outside views"))] : [],
    lines: [
      text(outside?.verdict) && line("Verdict", text(outside.verdict)),
      text(outside?.reason) && line("Why", text(outside.reason)),
    ].filter(Boolean),
  };

  const questions = uncertainties.filter((u) => text(u));
  const open = {
    stats: questions.length
      ? [stat(questions.length, plural(questions.length, "open question", "open questions"))]
      : [],
    lines: questions.length ? [line("Biggest", questions[0])] : [],
  };

  const resolves = method?.resolvesOn ? String(method.resolvesOn).slice(0, 10) : "";
  const methodology = {
    stats: [
      method?.sources ? stat(method.sources, plural(method.sources, "source", "sources")) : null,
      resolves ? { value: longDate(resolves), label: "", prefix: "Resolves" } : null,
    ].filter(Boolean),
    lines: [
      method?.forecaster && line("Forecaster", method.forecaster),
      (method?.sources || resolves) &&
        line("Inside", "Resolution criteria, every source, and the forecast method"),
    ].filter(Boolean),
  };

  const list = chains?.chains || [];
  const background = chains?.background || [];
  const chainSummary = { stats: [], lines: [] };
  if (chains?.judged) {
    // A judged report counts its insights and its background chains apart.
    if (list.length)
      chainSummary.stats.push(stat(list.length, plural(list.length, "finding", "findings")));
    if (background.length) chainSummary.stats.push(stat(background.length, "background"));
  } else if (list.length) {
    chainSummary.stats.push(
      stat(list.length, plural(list.length, "causal chain", "causal chains")),
    );
  }
  // The first two chains, numbered as the open section numbers them, each by its one-line
  // recap of what happens (its name when it has none); never its gate checks or verdict. The
  // insights lead; background chains (numbered after them) stand in only when there are none.
  (list.length ? list : background)
    .slice(0, 2)
    .forEach((c, i) => chainSummary.lines.push(line(`Chain ${i + 1}`, text(c.oneLine) || c.name)));

  return { chains: chainSummary, triggers, consensus, uncertainties: open, methodology };
}

// "more" on a reason adds this many sentences at most, each clipped to this length.
const MORE_SENTENCES = 2;
const MORE_CLIP = 260;
// A sentence is new when more than this share of its content words are not in the claim.
const NEW_SHARE = 0.6;

// A crude stem, so "widens" and "widen", or "error" and "errors", count as one word.
const stem = (w) => (/\d/.test(w) ? w : w.replace(/(?:ing|ed|es|s)$/, ""));
const contentWords = (s) =>
  String(s || "")
    .toLowerCase()
    .match(/[a-z0-9][a-z0-9.%']*[a-z0-9%]|[a-z0-9]/g)
    ?.filter((w) => w.length > 3 || /\d/.test(w))
    .map(stem) ?? [];

/** The text's sentences and bullet lines, one per entry, without "Key facts:" or bullets. */
function unitsOf(text) {
  return String(text || "")
    .replace(/^\s*Key facts:\s*/i, "")
    .split(/\n+/)
    .map((l) => l.trim().replace(/^[-*•]\s+/, ""))
    .flatMap((l) => l.split(/(?<=[.!?])\s+(?=[A-Z0-9"“(])/))
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * What "more" adds to a reason: the next sentence or two the reader hasn't read. The server's
 * detail restates the claim, so only its sentences that say something new count; failing those,
 * the opening facts of the cards the reason cites. Empty when nothing new is left.
 * @param {{claim: string, detail?: string}} item
 * @param {Array<{body?: string, summary?: string}>} [cards] the cards the reason cites
 * @returns {string}
 */
export function reasonMoreOf(item, cards = []) {
  const seen = new Set(contentWords(item?.claim));
  const isNew = (s) => {
    const words = contentWords(s);
    if (words.length < 3) return false;
    const fresh = words.filter((w) => !seen.has(w)).length;
    return fresh / words.length > NEW_SHARE;
  };
  const take = (units) => {
    const out = [];
    for (const u of units) {
      if (out.length >= MORE_SENTENCES) break;
      if (!isNew(u)) continue;
      out.push(u.length > MORE_CLIP ? `${u.slice(0, MORE_CLIP).replace(/\s+\S*$/, "")}…` : u);
      contentWords(u).forEach((w) => seen.add(w));
    }
    return out;
  };
  const fromDetail = take(unitsOf(item?.detail));
  const picked = fromDetail.length
    ? fromDetail
    : take((cards || []).flatMap((c) => unitsOf(c?.body || c?.summary)));
  return picked.join(" ");
}

/**
 * A side's reasons, strongest first (strong, moderate, weak, then unrated), keeping the
 * report's order within each strength. Each keeps its place in the report's column (`index`),
 * which is how a card's "Used by" names it.
 * @template T
 * @param {Array<T & {strength?: string}>} items
 * @returns {Array<{item: T, index: number}>}
 */
export function strongestFirst(items) {
  // Every spelling the reports use ("high", "strong", …) reads through strengthWord.
  const rank = (it) => ({ strong: 0, moderate: 1, weak: 2 })[strengthWord(it?.strength)] ?? 3;
  return (items || [])
    .map((item, index) => ({ item, index }))
    .sort((x, y) => rank(x.item) - rank(y.item) || x.index - y.index);
}
